import Marquee from "react-fast-marquee";
import {
  Couch,
  Snowflake,
  Leaf,
  HardHat,
  House,
  Bathtub,
  Car,
  Question,
  Buildings,
  Package,
  Briefcase,
  UsersThree,
  ShieldCheck,
  Clock,
  CurrencyDollar,
  Recycle,
  Lightning,
  Star,
  CheckCircle,
  MapPin,
  Phone,
  Truck,
  EnvelopeSimple,
  ArrowRight,
} from "@phosphor-icons/react";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { PaymentMethods } from "@/components/PaymentMethods";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const BASE_HOME_FAQS = [
  {
    q: "How much does junk removal cost in San Diego?",
    a: "Pricing is based on how much space your items take up in our truck, starting around $130 for a small load up to about $900 for a full 15-cubic-yard truck. You'll always get an upfront, no-obligation price before we start — no hidden fees.",
  },
  {
    q: "Do you offer same-day pickup?",
    a: "Yes — we offer same-day and next-day junk removal across San Diego County. Call or text (619) 999-3565 and we'll get you scheduled fast. Same-day pickup is available seven days a week.",
  },
  {
    q: "What items can't you take?",
    a: "We can't take hazardous materials such as paint, chemicals, solvents, motor oil, asbestos or medical waste — but we'll happily point you to the right drop-off. Just about everything else is fair game.",
  },
  {
    q: "Do you recycle or donate items?",
    a: "Yes. We sort every load and donate usable furniture and goods to local charities and recycle metal, wood, cardboard and e-waste, keeping as much as possible out of San Diego landfills.",
  },
  {
    q: "Which areas of San Diego County do you serve?",
    a: "All of San Diego County — including San Diego, La Jolla, Point Loma, Poway, Rancho Bernardo, Oceanside, Carlsbad, Encinitas and more. See our Areas We Serve section for the full list.",
  },
  {
    q: "Do you accept cards, ACH, Zelle, checks & cash?",
    a: "Yes — we accept all major payment methods: Visa, Mastercard, American Express, PayPal, Zelle, Venmo, Cash App, Apple Pay, ACH bank transfers, checks, and cash. No deposit required — you only pay once the job is done.",
  },
];
const HOME_FAQS = [...BASE_HOME_FAQS, ...FRED_HOME_FAQS, ...V5_HOME_FAQS];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: HOME_FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import QuoteForm from "@/components/QuoteForm";
import ReviewsSection from "@/components/ReviewsSection";
import GoogleReviews, { GoogleRatingBadge } from "@/components/GoogleReviews";
import OurWorkSection from "@/components/OurWorkSection";
import QuoteTool from "@/components/QuoteTool";
import ItemCalculator from "@/components/ItemCalculator";
import { SITE, HOME_TITLE, HOME_DESCRIPTION } from "@/seoData";
import { FRED_HOME_FAQS, HOME_EXTRA } from "@/seoContentV4";
import { V5_HOME_FAQS, V5_HOME_EXTRA } from "@/seoContentV5";

const HERO_BG =
  "https://images.unsplash.com/photo-1601467995997-ac1ae9a8fff4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const SERVICE_AREA_IMG =
  "https://images.pexels.com/photos/2962004/pexels-photo-2962004.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const HOW_IMG =
  "https://images.unsplash.com/photo-1523543659209-5c57c05834aa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
const CONSTRUCTION_IMG =
  "https://images.pexels.com/photos/19250066/pexels-photo-19250066.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const NAVY = "#15306E";
const GREEN = "#46A01E";

const BADGES = [
  { icon: ShieldCheck, t: "Licensed & Insured" },
  { icon: Clock, t: "Same-Day Service" },
  { icon: Recycle, t: "Eco-Friendly Disposal" },
  { icon: CurrencyDollar, t: "Upfront Pricing" },
  { icon: Lightning, t: "24/7 Availability" },
  { icon: Star, t: "5-Star Rated" },
];

const SERVICES = [
  { icon: Couch, t: "Furniture Removal", d: "Couches, mattresses, desks, dressers — hauled out and disposed responsibly.", span: "lg:col-span-4" },
  { icon: Snowflake, t: "Appliance Removal", d: "Fridges, washers, dryers, AC units, and water heaters removed safely.", span: "lg:col-span-4" },
  { icon: Leaf, t: "Yard Waste & Debris", d: "Branches, soil, fencing and green waste cleared after any project.", span: "lg:col-span-4" },
  { icon: HardHat, t: "Construction Debris", d: "Drywall, lumber, concrete and renovation waste — heavy lifting handled.", span: "lg:col-span-4", img: CONSTRUCTION_IMG },
  { icon: Truck, t: "Dump Truck For Hire With Driver", d: "Need to haul it yourself? Hire our dump truck with an experienced driver — by the load or by the day.", span: "lg:col-span-4" },
  { icon: House, t: "Estate & Foreclosure Cleanouts", d: "Full-property cleanouts handled with care, speed and discretion.", span: "lg:col-span-4" },
  { icon: Bathtub, t: "Hot Tub Removal", d: "We dismantle and haul away spas and hot tubs — no mess left behind.", span: "lg:col-span-4" },
  { icon: Car, t: "Garage Cleanouts", d: "Reclaim your garage from years of clutter in a single visit.", span: "lg:col-span-4" },
  { icon: Question, t: "Not Sure? Just Ask", d: "If it's not hazardous, we probably take it. Call and we'll tell you.", span: "lg:col-span-4" },
];

const CLIENTS = [
  { icon: Buildings, t: "Property Management" },
  { icon: House, t: "Residential & Commercial" },
  { icon: Package, t: "Storage Units" },
  { icon: Briefcase, t: "Offices" },
  { icon: UsersThree, t: "HOAs" },
];

const STEPS = [
  { n: "01", t: "Tell Us What You Have", d: "Send a quick request or call. Describe your items and location in San Diego County." },
  { n: "02", t: "Get An Upfront Quote", d: "We arrive on time, take a look, and give you a no-obligation, all-in price." },
  { n: "03", t: "We Haul It Away", d: "Approve the price and relax. We load, sweep up, and recycle or donate what we can." },
];

const WHY = [
  { icon: Clock, t: "Fast & On-Time", d: "Same-day and next-day appointments across the county." },
  { icon: CurrencyDollar, t: "Honest Pricing", d: "Transparent quotes before we lift a finger. No surprises." },
  { icon: Recycle, t: "Eco-Responsible", d: "We donate and recycle to keep junk out of San Diego landfills." },
  { icon: ShieldCheck, t: "Fully Insured", d: "Licensed, insured pros who treat your property with respect." },
];

const AREAS = [
  "San Diego", "La Jolla", "Chula Vista",
  "Oceanside", "Escondido", "Carlsbad", "Encinitas", "National City",
  "Poway", "Vista", "Coronado", "Mira Mesa",
  "Rancho Santa Fe", "Fairbanks Ranch", "4S Ranch",
];

const POPULAR = [
  { icon: House, t: "Foreclosure Cleanouts", d: "Fast, broom-clean cleanouts of bank-owned & foreclosed properties.", slug: "foreclosure-cleanout" },
  { icon: Buildings, t: "Whole-House Cleanouts", d: "Top-to-bottom clear-out of an entire home in a single visit.", slug: "whole-house-cleanout" },
  { icon: UsersThree, t: "HOA & Community Cleanouts", d: "Common-area, dumpster-overflow & abandoned-item removal for HOAs.", slug: "hoa-junk-removal" },
  { icon: House, t: "Estate Cleanouts", d: "Compassionate, discreet full-property estate cleanouts.", slug: "estate-cleanout" },
  { icon: House, t: "Household Cleanouts", d: "Clear clutter, old furniture and junk from any room in the home.", slug: "household-cleanout" },
  { icon: Briefcase, t: "Property Manager Cleanouts", d: "Reliable turnover & tenant leave-behind hauling for managers.", slug: "property-manager-cleanouts" },
  { icon: Car, t: "Garage Cleanouts", d: "Reclaim your garage from years of clutter in one visit.", slug: "garage-cleanout" },
  { icon: HardHat, t: "Construction Debris", d: "Drywall, lumber, concrete & renovation waste hauled away.", slug: "construction-debris-removal" },
  { icon: Package, t: "Hoarding Cleanouts", d: "Compassionate, discreet whole-home hoarding cleanups.", slug: "hoarding-cleanout" },
];

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`px-5 lg:px-8 ${className}`}>
    <div className="max-w-7xl mx-auto">{children}</div>
  </section>
);

const Eyebrow = ({ children }) => (
  <div className="inline-flex items-center gap-2 text-[#46A01E] font-display font-bold uppercase tracking-widest text-xs mb-4">
    <span className="h-px w-8 bg-[#46A01E]" />
    {children}
  </div>
);

export default function Landing() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
      }
    }
  }, [location]);

  return (
    <div className="bg-white text-[#0A0A0A] overflow-x-hidden">
      <Seo
        title={HOME_TITLE}
        description={HOME_DESCRIPTION}
        canonical={`${SITE}/`}
        jsonLd={[FAQ_JSONLD]}
      />
      <Header />

      {/* INSTANT ESTIMATE (top of page) */}
      <section id="estimate" className="px-5 lg:px-8 pt-28 sm:pt-44 lg:pt-56 pb-16 bg-[#0B1E45]">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-10">
            <div className="inline-flex items-center gap-2 text-[#5DB82F] font-display font-bold uppercase tracking-widest text-xs mb-4">
              <span className="h-px w-8 bg-[#5DB82F]" /> Instant Estimate
            </div>
            <p className="font-display font-black tracking-tighter text-4xl lg:text-5xl text-white">
              See roughly what your load will cost.
            </p>
            <p className="text-white/70 mt-4 text-lg leading-relaxed">
              Slide to match your load, or tap individual items — transparent, no-hidden-fees pricing
              updates instantly. Your final price is always confirmed on-site before we start.
            </p>
            <GoogleRatingBadge />
          </div>
          <div className="grid lg:grid-cols-2 gap-6 items-stretch">
            <QuoteTool />
            <ItemCalculator />
          </div>
          <div className="max-w-3xl mx-auto">
            <div className="mt-6 bg-white/95 rounded-lg px-5 py-4">
              <PaymentMethods />
            </div>
          </div>
        </div>
      </section>

      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-center pt-24 pb-16">
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="CleanOut247 junk removal truck" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0B1E45]/75" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 w-full grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 text-white blaze-rise">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-3 py-1.5 rounded-sm text-xs font-medium tracking-wide mb-6">
              <MapPin size={14} weight="fill" className="text-[#5DB82F]" />
              Proudly serving San Diego & all of San Diego County
            </div>
            <h1 className="font-display font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
              Junk Removal Near Me
              <br />
              <span className="text-[#5DB82F]">in San Diego</span>
            </h1>
            <p className="mt-6 text-lg text-white/80 max-w-xl leading-relaxed">
              Searching <strong className="font-semibold text-white">junk removal near me</strong>? CleanOut247 is a local
              San Diego junk removal company for homes and businesses across San Diego County. From a single couch to a
              whole estate or a <Link to="/services/commercial-junk-removal" className="text-white underline">commercial cleanout</Link>,
              our junk haulers lift it, load it, and haul it away — with upfront pricing and same-day pickup.
            </p>
            <p data-speakable className="mt-4 text-base text-white/90 font-semibold">
              Junk removal in San Diego starts at $130, with same-day service seven days a week.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href="tel:+16199993565"
                data-testid="hero-call-button"
                className="flex items-center gap-2 bg-[#46A01E] hover:bg-[#3A8418] text-white font-display font-extrabold uppercase tracking-wide px-7 py-4 rounded-sm transition-colors"
              >
                <Phone size={20} weight="fill" /> Call/Text (619) 999-3565
              </a>
              <button
                data-testid="hero-services-button"
                onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
                className="flex items-center gap-2 text-white font-display font-bold uppercase tracking-wide px-2 py-4 hover:text-[#5DB82F] transition-colors"
              >
                See What We Take <ArrowRight size={18} weight="bold" />
              </button>
            </div>
          </div>

          <div className="lg:col-span-5 blaze-rise" style={{ animationDelay: "0.15s" }}>
            <div className="bg-white rounded-sm p-6 lg:p-8 border-t-4 border-[#46A01E] shadow-2xl">
              <h2 className="font-display font-extrabold text-2xl tracking-tight text-[#15306E]">Get a Free Quote</h2>
              <p className="text-sm text-[#52525B] mt-1 mb-5">
                60-second form. We'll text or call you right back.
              </p>
              <QuoteForm variant="hero" />
            </div>
          </div>
        </div>
      </section>

      {/* TRUST MARQUEE */}
      <div className="bg-[#15306E] py-5">
        <Marquee gradient={false} speed={45} pauseOnHover>
          {[...BADGES, ...BADGES].map((b, i) => (
            <div key={i} className="flex items-center gap-2.5 mx-8 text-white/90">
              <b.icon size={20} weight="duotone" className="text-[#5DB82F]" />
              <span className="font-display font-semibold tracking-wide uppercase text-sm">{b.t}</span>
            </div>
          ))}
        </Marquee>
      </div>

      {/* SERVICES */}
      <Section id="services" className="py-20 lg:py-28">
        <div className="max-w-2xl mb-12">
          <Eyebrow>What We Haul</Eyebrow>
          <h2 className="font-display font-black tracking-tighter text-4xl lg:text-5xl text-[#15306E]">
            One call to CleanOut247 for everything you need gone.
          </h2>
          <p className="text-[#52525B] mt-4 text-lg leading-relaxed">
            Residential or commercial, big or small — if it's cluttering your space,
            we'll clear it out. Here's what San Diego trusts us with.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {SERVICES.map((s) => (
            <div
              key={s.t}
              data-testid={`service-card-${s.t}`}
              className={`group relative ${s.span} border border-[#E4E4E7] p-7 rounded-sm hover:border-[#46A01E] hover:-translate-y-1 transition-all overflow-hidden ${
                s.img ? "min-h-[260px] flex flex-col justify-end text-white" : "bg-white"
              }`}
            >
              {s.img && (
                <>
                  <img src={s.img} alt={s.t} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-[#0B1E45]/75 group-hover:bg-[#0B1E45]/65 transition-colors" />
                </>
              )}
              <div className="relative">
                <span className={`inline-flex p-2.5 rounded-sm mb-4 ${s.img ? "bg-[#46A01E]" : "bg-[#EAF4E3] group-hover:bg-[#46A01E]"} transition-colors`}>
                  <s.icon size={26} weight="duotone" className={s.img ? "text-white" : "text-[#46A01E] group-hover:text-white"} />
                </span>
                <h3 className={`font-display font-extrabold text-xl tracking-tight ${s.img ? "" : "text-[#15306E]"}`}>{s.t}</h3>
                <p className={`mt-2 text-sm leading-relaxed ${s.img ? "text-white/85 max-w-md" : "text-[#52525B]"}`}>{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* SAN DIEGO JUNK REMOVAL — local keyword sections */}
      <Section id="san-diego-junk-removal" className="py-20 lg:py-24 bg-[#F4FAF0]">
        <div className="grid lg:grid-cols-2 gap-10" data-testid="home-sd-sections">
          {[
            ["Same-day junk removal in San Diego", <>Our San Diego junk removal crews run seven days a week, so same-day pickup is usually available — call or text by early afternoon and we can often be there within hours.</>],
            ["Cheap junk removal in San Diego, with posted prices", <>From $130 for a curbside 1/8 load to about $900 for a full truck, plus single-item prices like a couch at $95. See the full <Link to="/junk-removal-cost" data-testid="home-link-cost" className="text-[#46A01E] font-semibold hover:underline">junk removal cost</Link> list, or compare <Link to="/pricing-and-disposal-guide" className="text-[#46A01E] font-semibold hover:underline">San Diego dump fees</Link>.</>],
            ["Junk removal services in San Diego", <>San Diego junk pickup for <Link to="/services/mattress-removal" className="text-[#46A01E] font-semibold hover:underline">mattresses</Link>, <Link to="/services/couch-removal" className="text-[#46A01E] font-semibold hover:underline">couches</Link>, <Link to="/services/appliance-removal" className="text-[#46A01E] font-semibold hover:underline">appliances</Link>, <Link to="/services/garage-cleanout" className="text-[#46A01E] font-semibold hover:underline">garage cleanouts</Link>, <Link to="/services/estate-cleanout" className="text-[#46A01E] font-semibold hover:underline">estate cleanup</Link> and <Link to="/services/hot-tub-removal" className="text-[#46A01E] font-semibold hover:underline">hot tubs</Link>.</>],
            ["Hauling services across San Diego County", <>From Carmel Valley, Mira Mesa and Clairemont to La Mesa, Poway and the coast — see every <Link to="/service-areas" className="text-[#46A01E] font-semibold hover:underline">area we serve</Link>.</>],
            ...[...HOME_EXTRA, ...V5_HOME_EXTRA].map((x) => [x.h2, x.text]),
          ].map(([h, body]) => (
            <div key={h}>
              <h2 className="font-display font-black tracking-tighter text-2xl lg:text-3xl text-[#15306E]">{h}</h2>
              <p className="text-[#52525B] mt-3 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* RESIDENTIAL / COMMERCIAL / SAME-DAY */}
      <Section id="residential-commercial" className="py-20 lg:py-28">
        <div className="max-w-2xl mb-12">
          <Eyebrow>How We Help</Eyebrow>
          <h2 className="font-display font-black tracking-tighter text-4xl lg:text-5xl text-[#15306E]">
            Residential & commercial junk removal in San Diego.
          </h2>
          <p className="text-[#52525B] mt-4 text-lg leading-relaxed">
            Whatever you need gone, we've got a truck and a friendly crew for it — homes,
            apartments, offices and rentals across San Diego County. Comparing junk removal companies in San Diego?
            We're locally run, licensed and insured, and quote every job upfront. Affordable, reliable and eco-friendly junk removal —
            with same-day and next-day pickup. Curious about{" "}
            <Link to="/junk-removal-cost" data-testid="home-link-pricing" className="text-[#46A01E] font-semibold hover:underline">junk removal prices in San Diego</Link>? It's all upfront.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="border border-[#E4E4E7] rounded-sm p-7">
            <h3 className="font-display font-extrabold text-xl text-[#15306E]">Residential Junk Removal</h3>
            <p className="mt-3 text-[#52525B] leading-relaxed text-sm">
              Household junk, furniture, appliances, garage and whole-home cleanouts. We handle the heavy
              lifting from any room, then sweep up. Popular jobs include{" "}
              <Link to="/services/furniture-removal" data-testid="home-link-furniture" className="text-[#46A01E] font-semibold hover:underline">furniture removal</Link>,{" "}
              <Link to="/services/couch-removal" data-testid="home-link-couch" className="text-[#46A01E] font-semibold hover:underline">couch removal</Link>,{" "}
              full <Link to="/services/household-cleanout" data-testid="home-link-household" className="text-[#46A01E] font-semibold hover:underline">household cleanouts</Link>, and{" "}
              <Link to="/services/real-estate-cleanout" data-testid="home-link-estate" className="text-[#46A01E] font-semibold hover:underline">estate cleanouts</Link>.
            </p>
          </div>
          <div className="border border-[#E4E4E7] rounded-sm p-7">
            <h3 className="font-display font-extrabold text-xl text-[#15306E]">Commercial Junk Removal</h3>
            <p className="mt-3 text-[#52525B] leading-relaxed text-sm">
              <Link to="/services/commercial-junk-removal" data-testid="home-link-commercial" className="text-[#46A01E] font-semibold hover:underline">Commercial junk removal</Link> for
              offices, retail stores, restaurants,{" "}
              <Link to="/services/warehouse-cleanout" data-testid="home-link-warehouse" className="text-[#46A01E] font-semibold hover:underline">warehouse cleanouts</Link>,{" "}
              <Link to="/services/property-manager-cleanouts" data-testid="home-link-property-managers" className="text-[#46A01E] font-semibold hover:underline">property managers</Link>, HOAs and contractor job sites —
              from <Link to="/services/office-furniture-removal" data-testid="home-link-office-furniture" className="text-[#46A01E] font-semibold hover:underline">office furniture removal</Link> to{" "}
              <Link to="/services/apartment-eviction-trashout" className="text-[#46A01E] font-semibold hover:underline">apartment trash-outs</Link> and{" "}
              <Link to="/services/construction-debris-removal" className="text-[#46A01E] font-semibold hover:underline">construction debris</Link>.
            </p>
          </div>
          <div className="border border-[#E4E4E7] rounded-sm p-7">
            <h3 className="font-display font-extrabold text-xl text-[#15306E]">Same-Day & Scheduled Pickup</h3>
            <p className="mt-3 text-[#52525B] leading-relaxed text-sm">
              Need it gone today? Our same-day and daily junk removal service across San Diego County has
              you covered, or book a scheduled pickup that fits your day.
            </p>
          </div>
        </div>
        <p className="mt-8 text-[#52525B]">
          Serving the whole county — explore the{" "}
          <Link to="/junk-removal/san-diego" data-testid="home-link-sd-hub" className="text-[#46A01E] font-semibold hover:underline">San Diego neighborhoods we serve</Link>.
        </p>
      </Section>

      {/* POPULAR CLEANOUTS — internal links to high-ticket SEO pages */}
      <Section id="popular" className="py-20 lg:py-28 bg-[#F4FAF0] border-y border-[#E4E4E7]">
        <div className="max-w-2xl mb-12">
          <Eyebrow>Popular Cleanouts</Eyebrow>
          <h2 className="font-display font-black tracking-tighter text-4xl lg:text-5xl text-[#15306E]">
            Big jobs, done right — explore our top services.
          </h2>
          <p className="text-[#52525B] mt-4 text-lg leading-relaxed">
            From foreclosures and estates to whole-house and HOA cleanouts, these are the
            projects San Diego property owners and managers call us for most.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {POPULAR.map((p) => (
            <Link
              key={p.slug}
              to={`/services/${p.slug}`}
              data-testid={`popular-service-${p.slug}`}
              className="group bg-white border border-[#E4E4E7] p-7 rounded-sm hover:border-[#46A01E] hover:-translate-y-1 transition-all flex flex-col"
            >
              <span className="inline-flex p-2.5 rounded-sm mb-4 bg-[#EAF4E3] group-hover:bg-[#46A01E] transition-colors w-fit">
                <p.icon size={26} weight="duotone" className="text-[#46A01E] group-hover:text-white transition-colors" />
              </span>
              <h3 className="font-display font-extrabold text-lg tracking-tight text-[#15306E]">{p.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#52525B] flex-1">{p.d}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-[#46A01E] font-display font-bold text-sm uppercase tracking-wide">
                Learn More <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          ))}
        </div>

        <div className="mt-10">
          <Link
            to="/service-areas"
            data-testid="popular-view-all-areas"
            className="inline-flex items-center gap-2 text-[#15306E] font-display font-bold uppercase tracking-wide hover:text-[#46A01E] transition-colors"
          >
            See all San Diego County service areas <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </Section>

      {/* WHO WE WORK WITH */}
      <Section className="py-16 bg-[#F4FAF0] border-y border-[#E4E4E7]">
        <Eyebrow>Who We Work With</Eyebrow>
        <h2 className="font-display font-black tracking-tighter text-3xl lg:text-4xl text-[#15306E] max-w-2xl">
          Trusted by homeowners, businesses & property pros.
        </h2>
        <p className="text-[#52525B] mt-3 text-lg max-w-xl">
          From a single apartment to entire portfolios — Matt is the on-call hauler for:
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-10">
          {CLIENTS.map((c) => (
            <div
              key={c.t}
              data-testid={`client-${c.t}`}
              className="bg-white border border-[#E4E4E7] rounded-sm p-6 flex flex-col items-center text-center gap-3 hover:border-[#46A01E] hover:-translate-y-1 transition-all"
            >
              <c.icon size={36} weight="duotone" className="text-[#46A01E]" />
              <span className="font-display font-bold text-sm text-[#15306E] leading-tight">{c.t}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section id="how" className="py-20 lg:py-28 bg-[#F4F4F5]">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="relative">
            <img src={HOW_IMG} alt="Matt at work for CleanOut247" className="w-full h-[420px] object-cover rounded-sm border border-[#E4E4E7]" />
            <div className="absolute -bottom-6 -right-2 lg:right-6 bg-[#46A01E] text-white p-6 rounded-sm max-w-[220px]">
              <Truck size={32} weight="fill" />
              <p className="font-display font-extrabold text-lg leading-tight mt-2">Same-day pickups, 7 days a week.</p>
            </div>
          </div>
          <div>
            <Eyebrow>How It Works</Eyebrow>
            <h2 className="font-display font-black tracking-tighter text-4xl lg:text-5xl text-[#15306E]">
              Three steps. Zero hassle.
            </h2>
            <div className="mt-8 space-y-2">
              {STEPS.map((st) => (
                <div key={st.n} data-testid={`step-${st.n}`} className="flex gap-5 py-5 border-b border-[#E4E4E7] last:border-0">
                  <span className="font-display font-black text-3xl text-[#46A01E] tracking-tighter">{st.n}</span>
                  <div>
                    <h3 className="font-display font-extrabold text-xl tracking-tight text-[#15306E]">{st.t}</h3>
                    <p className="text-[#52525B] mt-1 leading-relaxed">{st.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* OUR WORK GALLERY */}
      <OurWorkSection />

      {/* WHY US */}
      <Section id="why" className="py-20 lg:py-28">
        <div className="max-w-2xl mb-12">
          <Eyebrow>Why CleanOut247</Eyebrow>
          <h2 className="font-display font-black tracking-tighter text-4xl lg:text-5xl text-[#15306E]">
            The dependable name San Diego calls back.
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {WHY.map((w) => (
            <div key={w.t} data-testid={`why-card-${w.t}`} className="border border-[#E4E4E7] p-7 rounded-sm hover:border-[#46A01E] hover:-translate-y-1 transition-all">
              <w.icon size={32} weight="duotone" className="text-[#46A01E]" />
              <h3 className="font-display font-extrabold text-lg tracking-tight mt-4 text-[#15306E]">{w.t}</h3>
              <p className="text-sm text-[#52525B] mt-2 leading-relaxed">{w.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* SERVICE AREAS */}
      <Section id="areas" className="py-20 lg:py-28 bg-[#15306E] text-white">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[#5DB82F] font-display font-bold uppercase tracking-widest text-xs mb-4">
              <span className="h-px w-8 bg-[#5DB82F]" /> Service Areas
            </div>
            <h2 className="font-display font-black tracking-tighter text-4xl lg:text-5xl">
              Covering all of San Diego County.
            </h2>
            <p className="text-white/70 mt-4 text-lg leading-relaxed">
              From the coast to the canyons, our trucks roll across the whole county.
              Don't see your neighborhood? Give us a call — we likely serve it too.
            </p>
            <div className="mt-8 flex flex-wrap gap-2.5">
              {AREAS.map((a) => (
                <span key={a} data-testid={`area-${a}`} className="flex items-center gap-1.5 border border-white/20 px-3 py-1.5 rounded-sm text-sm text-white/90">
                  <MapPin size={14} weight="fill" className="text-[#5DB82F]" /> {a}
                </span>
              ))}
            </div>
          </div>
          <div>
            <img src={SERVICE_AREA_IMG} alt="San Diego skyline" className="w-full h-[420px] object-cover rounded-sm" />
          </div>
        </div>
      </Section>

      {/* REVIEWS */}
      <GoogleReviews />
      <ReviewsSection />

      {/* FAQ */}
      <Section id="faq" className="py-20 lg:py-28">
        <div className="max-w-2xl mb-12">
          <Eyebrow>FAQ</Eyebrow>
          <h2 className="font-display font-black tracking-tighter text-4xl lg:text-5xl text-[#15306E]">
            Questions? We've got answers.
          </h2>
          <p className="text-[#52525B] mt-4 text-lg leading-relaxed">
            The stuff San Diego asks us most — from pricing to payment methods.
          </p>
        </div>
        <div className="max-w-3xl">
          <Accordion type="single" collapsible className="w-full" data-testid="home-faq">
            {HOME_FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="border-b border-[#E4E4E7]">
                <AccordionTrigger className="text-left font-display font-bold text-lg text-[#15306E] hover:text-[#46A01E] hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#52525B] text-base leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      {/* FINAL CTA */}
      <Section className="py-20 lg:py-28 bg-[#F4F4F5]">
        <div className="bg-[#15306E] rounded-sm p-10 lg:p-16 text-white grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display font-black tracking-tighter text-4xl lg:text-5xl leading-[0.95]">
              Ready to <span className="text-[#5DB82F]">clear it out?</span>
            </h2>
            <p className="mt-4 text-white/85 text-lg leading-relaxed max-w-md">
              Get a free, no-obligation quote today. Same-day service across San Diego County.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="tel:+16199993565" data-testid="cta-call-button" className="flex items-center gap-2 bg-[#46A01E] text-white font-display font-extrabold uppercase tracking-wide px-7 py-4 rounded-sm hover:bg-[#3A8418] transition-colors">
                <Phone size={20} weight="fill" /> Call/Text (619) 999-3565
              </a>
              <a href="mailto:office@cleanout247.com" data-testid="cta-email-button" className="flex items-center gap-2 border border-white/60 text-white font-display font-bold uppercase tracking-wide px-7 py-4 rounded-sm hover:bg-white/10 transition-colors">
                <EnvelopeSimple size={20} weight="fill" /> Email Us
              </a>
            </div>
            <ul className="mt-8 space-y-2">
              {["Upfront, all-in pricing", "Licensed & fully insured", "Eco-friendly disposal"].map((p) => (
                <li key={p} className="flex items-center gap-2 text-white/90">
                  <CheckCircle size={20} weight="fill" className="text-[#5DB82F]" /> {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white rounded-sm p-6 lg:p-8 text-[#0A0A0A]">
            <h3 className="font-display font-extrabold text-2xl tracking-tight mb-4 text-[#15306E]">Request Your Quote</h3>
            <QuoteForm variant="cta" />
          </div>
        </div>
      </Section>

      {/* FOOTER */}
      <Footer />
    </div>
  );
}
